import { getMiddleCenterBias, roundedRect } from '@glideapps/glide-data-grid';
import type { DrawArgs, Rectangle } from '@glideapps/glide-data-grid';
import type { CustomCell } from '@glideapps/glide-data-grid';
import type { Warmth } from '@nemo/core/model';
import { paletteValue } from '../../palette.js';

export type ChipTone = Warmth | 'neutral' | 'busy' | 'good' | 'bad';

export interface Chip {
  readonly label: string;
  readonly tone: ChipTone;
  /**
   * `ringed` keeps the tone's fill and adds a ring around it: a chip carrying
   * a machine's fact as well as a person's, so the two can be told apart at a
   * glance without a second colour scale competing with warmth. `hollow`
   * drops the fill altogether: a value nobody typed and nobody can edit here,
   * observed rather than claimed.
   */
  readonly style?: 'ringed' | 'hollow' | undefined;
  /**
   * `lead` puts a filled dot in ink before the label: the stage chip of a
   * fund that leads at that stage, not only invests there.
   */
  readonly mark?: 'lead' | undefined;
  /**
   * `conflict` adds a small "!" in the attention colours after the label: a
   * person's answer here that newer evidence disagrees with.
   */
  readonly flag?: 'conflict' | undefined;
}

export interface ToneColours {
  readonly background: string;
  readonly text: string;
}

/**
 * Warm to cold, then the grey for "nobody has rated this", then the neutral a
 * structural label takes, then the three a job's state needs: working,
 * finished, broken. Each names the pair of custom properties the palette
 * defines it as; the fund pane's warmth pills hand these straight to the
 * browser as `var(…)`, and the canvas resolves them once.
 */
export const TONE_VARS: Record<ChipTone, ToneColours> = {
  strong: { background: '--warmth-strong-bg', text: '--warmth-strong-text' },
  warm: { background: '--warmth-warm-bg', text: '--warmth-warm-text' },
  lukewarm: { background: '--warmth-lukewarm-bg', text: '--warmth-lukewarm-text' },
  cold: { background: '--warmth-cold-bg', text: '--warmth-cold-text' },
  unknown: { background: '--warmth-not-rated-bg', text: '--warmth-not-rated-text' },
  neutral: { background: '--chip-bg', text: '--chip-text' },
  busy: { background: '--attention-bg', text: '--attention-text' },
  good: { background: '--warmth-strong-bg', text: '--warmth-strong-text' },
  bad: { background: '--danger-soft', text: '--danger' },
};

/** What one tone resolves to right now, for a surface that cannot cascade. */
export function toneColours(tone: ChipTone): ToneColours {
  return {
    background: paletteValue(TONE_VARS[tone].background),
    text: paletteValue(TONE_VARS[tone].text),
  };
}

const PADDING = 8;
const GAP = 6;
const HEIGHT = 20;
/**
 * Shape says what kind of chip it is before colour does: a label the fund
 * carries (stage, type, region) is squared off, a judgement or a state (a
 * warmth, a job) is fully round. The same split the DOM draws with
 * `--radius-xs` and `--radius-pill`.
 */
function radiusOf(tone: ChipTone): number {
  return tone === 'neutral' ? 4 : HEIGHT / 2;
}
const DOT = 6;
const DOT_GAP = 4;
const FLAG = 12;

/** The row height the density toggle's roomier setting asks the grid for. */
export const TWO_LINE_ROW_HEIGHT = 56;

/**
 * How many lines of text this cell has room for. A cell never decides the row
 * height; it reads the one the grid was given, so the toggle in the toolbar is
 * the only thing that says how tall a row is.
 */
export function linesIn(rect: Rectangle): 1 | 2 {
  return rect.height >= TWO_LINE_ROW_HEIGHT - 8 ? 2 : 1;
}

/** Where the baseline of each of `count` stacked lines sits in the cell. */
export function lineCentres(rect: Rectangle, count: number): number[] {
  const step = rect.height / count;
  return Array.from({ length: count }, (_, index) => rect.y + step * (index + 0.5));
}

export interface ChipsLayout {
  /**
   * Muted text beside the chips: what the cell says over and above them, such
   * as how many more pairings there are. It takes the second line where the
   * row is tall enough for one, and trails the chips where it is not.
   */
  readonly note?: string | undefined;
}

/**
 * Chips laid out left to right and clipped at the cell edge: the same painting
 * for relationships, picklists and any other list of short labels.
 */
export function paintChips(
  args: DrawArgs<CustomCell>,
  chips: readonly Chip[],
  layout: ChipsLayout = {},
): void {
  const { ctx, rect, theme } = args;
  const note = layout.note;
  if (chips.length === 0 && (note === undefined || note === '')) return;

  ctx.save();
  ctx.beginPath();
  ctx.rect(rect.x, rect.y, rect.width, rect.height);
  ctx.clip();

  const bias = getMiddleCenterBias(ctx, theme);
  const noteOnItsOwnLine = note !== undefined && note !== '' && linesIn(rect) === 2;
  const centres = lineCentres(rect, noteOnItsOwnLine ? 2 : 1);
  const middle = centres[0] ?? rect.y + rect.height / 2;
  const left = rect.x + theme.cellHorizontalPadding;
  const limit = rect.x + rect.width - theme.cellHorizontalPadding;
  let x = left;

  for (const chip of chips) {
    const marked = chip.mark === 'lead';
    const markWidth = marked ? DOT + DOT_GAP : 0;
    const flagged = chip.flag === 'conflict';
    const flagWidth = flagged ? DOT_GAP + FLAG : 0;
    const labelWidth = ctx.measureText(chip.label).width;
    const width = labelWidth + PADDING * 2 + markWidth + flagWidth;
    if (x + width > limit && x > left) {
      // Nothing more fits; the cell says so rather than painting half a chip.
      ctx.fillStyle = theme.textLight;
      ctx.fillText('…', x, middle + bias);
      x += ctx.measureText('…').width + GAP;
      break;
    }

    const tone = toneColours(chip.tone);
    if (chip.style !== 'hollow') {
      ctx.fillStyle = tone.background;
      ctx.beginPath();
      roundedRect(ctx, x, middle - HEIGHT / 2, width, HEIGHT, radiusOf(chip.tone));
      ctx.fill();
    }
    if (chip.style !== undefined) {
      ctx.strokeStyle = tone.text;
      ctx.lineWidth = 1;
      ctx.beginPath();
      roundedRect(
        ctx,
        x + 0.5,
        middle - HEIGHT / 2 + 0.5,
        width - 1,
        HEIGHT - 1,
        radiusOf(chip.tone),
      );
      ctx.stroke();
    }

    if (marked) {
      ctx.fillStyle = paletteValue('--text');
      ctx.beginPath();
      ctx.arc(x + PADDING + DOT / 2, middle, DOT / 2, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.fillStyle = tone.text;
    ctx.fillText(chip.label, x + PADDING + markWidth, middle + bias);

    if (flagged) {
      const centre = x + PADDING + markWidth + labelWidth + DOT_GAP + FLAG / 2;
      ctx.fillStyle = paletteValue('--attention-bg');
      ctx.beginPath();
      ctx.arc(centre, middle, FLAG / 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = paletteValue('--attention-text');
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.fillStyle = paletteValue('--attention-text');
      ctx.fillText('!', centre - ctx.measureText('!').width / 2, middle + bias);
    }
    x += width + GAP;
  }

  if (note !== undefined && note !== '') {
    ctx.fillStyle = theme.textLight;
    const y = noteOnItsOwnLine ? (centres[1] ?? middle) : middle;
    ctx.fillText(note, noteOnItsOwnLine ? left : x, y + bias);
  }

  ctx.restore();
}

/**
 * Stacked lines of plain text, clipped to the row's height. A line the row has
 * no room for is not silently dropped: the last one it does paint carries a
 * muted "+N more" naming how many are behind it in total, not merely how many
 * of the lines handed in did not fit.
 */
export function paintLines(
  args: DrawArgs<CustomCell>,
  lines: readonly string[],
  empty: string,
  totalCount?: number,
): void {
  const { ctx, rect, theme } = args;

  ctx.save();
  ctx.beginPath();
  ctx.rect(rect.x, rect.y, rect.width, rect.height);
  ctx.clip();

  const bias = getMiddleCenterBias(ctx, theme);
  const left = rect.x + theme.cellHorizontalPadding;

  if (lines.length === 0) {
    ctx.fillStyle = theme.textLight;
    const centre = lineCentres(rect, 1)[0] ?? rect.y + rect.height / 2;
    ctx.fillText(empty, left, centre + bias);
    ctx.restore();
    return;
  }

  const total = totalCount ?? lines.length;
  const room = Math.min(linesIn(rect), lines.length);
  const centres = lineCentres(rect, room);
  const more = total - room;

  for (const [index, centre] of centres.entries()) {
    const last = index === room - 1;
    const line = lines[index] ?? '';
    ctx.fillStyle = theme.textDark;
    ctx.fillText(line, left, centre + bias);
    if (last && more > 0) {
      const suffix = ` · +${more} more`;
      ctx.fillStyle = theme.textLight;
      ctx.fillText(suffix, left + ctx.measureText(line).width, centre + bias);
    }
  }

  ctx.restore();
}
